import React, { useState } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { useRef } from "react";

const CopySalary = () => {
    const toast = useRef(null);

    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const months = [
        { label: "January", value: 1 },
        { label: "February", value: 2 },
        { label: "March", value: 3 },
        { label: "April", value: 4 },
        { label: "May", value: 5 },
        { label: "June", value: 6 },
        { label: "July", value: 7 },
        { label: "August", value: 8 },
        { label: "September", value: 9 },
        { label: "October", value: 10 },
        { label: "November", value: 11 },
        { label: "December", value: 12 }
    ];

    const getPreviousMonth = () => {
        const date = new Date(year, month - 1, 1);

        date.setMonth(date.getMonth() - 1);

        return date.toLocaleString("en-IN", {
            month: "long",
            year: "numeric"
        });
    };

    const getCurrentMonth = () => {
        const date = new Date(year, month - 1, 1);

        return date.toLocaleString("en-IN", {
            month: "long",
            year: "numeric"
        });
    };

    const copyPreviousMonthSalary = async () => {
        if (!month || !year) {
            toast.current?.show({
                severity: "warn",
                summary: "Validation",
                detail: "Please select month and year.",
                life: 3000
            });

            return;
        }

        const confirmed = window.confirm(
            `Copy salary settings from ${getPreviousMonth()} to ${getCurrentMonth()}?`
        );

        if (!confirmed) {
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const token = localStorage.getItem("accessToken");

            const apiUrl =
                `${process.env.REACT_APP_API_PATH}` +
                `Salary/copy-previous-month-salary` +
                `?month=${month}&year=${year}`;

            const response = await fetch(apiUrl, {
                method: "POST",

                headers: {
                    "Content-Type": "application/json",

                    ...(token
                        ? {
                              Authorization: `Bearer ${token}`
                          }
                        : {})
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data?.message ||
                    data ||
                    "Unable to copy salary settings."
                );
            }

            setResult(data);

            toast.current?.show({
                severity: "success",
                summary: "Success",
                detail:
                    data.message ||
                    "Salary settings copied successfully.",
                life: 5000
            });
        } catch (error) {
            console.error("Copy salary error:", error);

            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: error.message || "Something went wrong.",
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-3">

            <Toast ref={toast} />

            <Card
                title="Copy Previous Month Salary"
                subTitle="Generate recurring salary settings for the selected month"
            >

                <div className="grid">

                    {/* Month */}
                    <div className="col-12 md:col-4">

                        <label
                            htmlFor="salaryMonth"
                            className="block mb-2 font-semibold"
                        >
                            Salary Month
                        </label>

                        <Dropdown
                            id="salaryMonth"
                            value={month}
                            options={months}
                            onChange={(e) => setMonth(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select Month"
                            className="w-full"
                        />

                    </div>


                    {/* Year */}
                    <div className="col-12 md:col-4">

                        <label
                            htmlFor="salaryYear"
                            className="block mb-2 font-semibold"
                        >
                            Salary Year
                        </label>

                        <InputNumber
                            id="salaryYear"
                            value={year}
                            onValueChange={(e) => setYear(e.value)}
                            min={2000}
                            max={2100}
                            useGrouping={false}
                            className="w-full"
                            inputClassName="w-full"
                        />

                    </div>


                    {/* Previous Month */}
                    <div className="col-12 md:col-4">

                        <label className="block mb-2 font-semibold">
                            Previous Month
                        </label>

                        <div className="p-inputtext p-component w-full">
                            {getPreviousMonth()}
                        </div>

                    </div>

                </div>


                {/* Information */}
                <div className="mt-3">

                    <Message
                        severity="info"
                        text={
                            `Recurring salary settings from ${getPreviousMonth()} ` +
                            `will be copied to ${getCurrentMonth()}.`
                        }
                    />

                </div>


                {/* Button */}
                <div className="flex justify-content-end mt-4">

                    <Button
                        label={
                            loading
                                ? "Processing..."
                                : "Copy Previous Month Salary"
                        }
                        icon={
                            loading
                                ? "pi pi-spin pi-spinner"
                                : "pi pi-copy"
                        }
                        severity="primary"
                        onClick={copyPreviousMonthSalary}
                        disabled={loading}
                    />

                </div>


                {/* Result */}
                {result && (
                    <div className="mt-4">

                        <Message
                            severity="success"
                            content={
                                <div>

                                    <div className="font-bold mb-2">
                                        Salary settings generated successfully
                                    </div>

                                    <div>
                                        Previous Month:{" "}
                                        {months.find(
                                            x =>
                                                x.value ===
                                                result.previousMonth
                                        )?.label}{" "}
                                        {result.previousYear}
                                    </div>

                                    <div>
                                        Current Month:{" "}
                                        {months.find(
                                            x =>
                                                x.value ===
                                                result.currentMonth
                                        )?.label}{" "}
                                        {result.currentYear}
                                    </div>

                                    <div>
                                        Records Created:{" "}
                                        <strong>
                                            {result.insertedRows}
                                        </strong>
                                    </div>

                                </div>
                            }
                        />

                    </div>
                )}

            </Card>

        </div>
    );
};

export default CopySalary;